import type { z } from 'zod';

import { clearSession, getSession } from '../auth/session-store';
import { createRequestTransport } from './transport';

export { ApiError } from './transport';

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Wired once by the app shell to redirect to /entrar on any 401. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

const request = createRequestTransport({
  getSession,
  onUnauthorized: () => {
    clearSession();
    unauthorizedHandler?.();
  },
});

export function apiGet<TSchema extends z.ZodType>(path: string, schema: TSchema) {
  return request(path, schema, { method: 'GET' });
}

export function apiPost<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body?: unknown,
  idempotencyKey?: string,
) {
  return request(path, schema, {
    method: 'POST',
    body: body ?? {},
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/** Full-state upsert (PUT semantics): idempotent by body, no Idempotency-Key ledger. */
export function apiPut<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  idempotencyKey?: string,
) {
  return request(path, schema, {
    method: 'PUT',
    body,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export function apiPatch<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  idempotencyKey: string,
) {
  return request(path, schema, {
    method: 'PATCH',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function apiDelete<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  idempotencyKey: string,
) {
  return request(path, schema, {
    method: 'DELETE',
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function apiUpload<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  formData: FormData,
  idempotencyKey: string,
) {
  return request(path, schema, {
    multipart: true,
    method: 'POST',
    body: formData,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** Full-state multipart upsert (PUT semantics), for the floor-plan image. */
export function apiUploadPut<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  formData: FormData,
  idempotencyKey: string,
) {
  return request(path, schema, {
    multipart: true,
    method: 'PUT',
    body: formData,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}
